import React, { useState } from 'react';
import { ENVIRONMENT_STYLES, TRANSFORMATION_STYLES } from '../../utils/constants';
import StyleThumbnail from '../shared/StyleThumbnail';

const StyleSelectionScreen = ({ capturedPhoto, onStyleSelect, onBack }) => {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [generationMode, setGenerationMode] = useState('environment'); // 'environment' or 'transformation'

  // Get the appropriate styles based on generation mode
  const STYLES = generationMode === 'environment' ? ENVIRONMENT_STYLES : TRANSFORMATION_STYLES;
  const styleKeys = Object.keys(STYLES);
  const ITEMS_PER_PAGE = 8; // 4 columns × 2 rows
  const totalPages = Math.ceil(styleKeys.length / ITEMS_PER_PAGE);

  // Get styles for current page
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentStyles = styleKeys.slice(startIndex, endIndex);

  const handleStyleClick = (styleKey) => {
    setSelectedStyle(styleKey);
  };

  const handleModeChange = (mode) => {
    setGenerationMode(mode);
    setSelectedStyle(null); // Reset selection when mode changes
    setCurrentPage(0); // Reset to first page
  };

  const handleContinue = () => {
    if (selectedStyle) {
      // Pass style with generation mode
      onStyleSelect({
        ...STYLES[selectedStyle],
        generationMode: generationMode
      });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col px-4 sm:px-6 py-[1vh] overflow-hidden">
      {/* Captured Photo at Top - 2:3 aspect ratio (responsive sizing) */}
      <div className="flex-shrink-0 text-center mb-[0.5vh]">
        <div className="relative w-full mx-auto" style={{ maxWidth: 'min(250px, 20vw, 15vh)', aspectRatio: '2/3' }}>
          <img
            src={capturedPhoto}
            alt="Captured"
            className="w-full h-full object-cover rounded-lg shadow-lg border-2 border-[#e5181e]/40"
            style={{ aspectRatio: '2/3' }}
          />
        </div>
      </div>

      {/* Generation Mode Selector */}
      <div className="flex-shrink-0 mb-[1vh]">
        <div className="max-w-2xl mx-auto bg-slate-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 border border-[#e5181e]/30">
          <h2 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1 sm:mb-2 text-center">Choose Your Style</h2>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {/* Environment Only Mode */}
            <button
              onClick={() => handleModeChange('environment')}
              className={`
                relative p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl transition-all transform active:scale-95
                ${generationMode === 'environment'
                  ? 'bg-[#e5181e] border-2 border-[#e5181e] shadow-lg scale-105'
                  : 'bg-slate-700/50 border-2 border-slate-600 hover:border-[#e5181e]/50'
                }
              `}
            >
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl mb-1">🌍</div>
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-white mb-0.5">Environment Only</h3>
                <p className={`text-[10px] sm:text-xs md:text-sm ${generationMode === 'environment' ? 'text-white' : 'text-gray-300'}`}>
                  Change background, keep you as-is
                </p>
              </div>
              {generationMode === 'environment' && (
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center">
                    <span className="text-[#e5181e] text-xs sm:text-sm">✓</span>
                  </div>
                </div>
              )}
            </button>

            {/* Full Transformation Mode */}
            <button
              onClick={() => handleModeChange('transformation')}
              className={`
                relative p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl transition-all transform active:scale-95
                ${generationMode === 'transformation'
                  ? 'bg-[#e5181e] border-2 border-[#e5181e] shadow-lg scale-105'
                  : 'bg-slate-700/50 border-2 border-slate-600 hover:border-[#e5181e]/50'
                }
              `}
            >
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl mb-1">✨</div>
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-white mb-0.5">Full Transformation</h3>
                <p className={`text-[10px] sm:text-xs md:text-sm ${generationMode === 'transformation' ? 'text-white' : 'text-gray-300'}`}>
                  Become a character in the scene
                </p>
              </div>
              {generationMode === 'transformation' && (
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center">
                    <span className="text-[#e5181e] text-xs sm:text-sm">✓</span>
                  </div>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Headline and Subheadline */}
      <div className="flex-shrink-0 text-center mb-[0.5vh]">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-0.5 sm:mb-1 tracking-wide">
          CHOOSE YOUR SCENE
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-gray-200 font-medium">
          {generationMode === 'environment' ? 'SELECT A BACKGROUND' : 'SELECT A WORLD TO JOIN'}
        </p>
      </div>

      {/* Action Controls - Positioned above grid for better accessibility */}
      <div className="flex-shrink-0 flex justify-center items-center gap-2 sm:gap-3 mb-[1vh] sm:mb-[1.5vh] relative z-20">
        <button
          onClick={onBack}
          className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gray-700 text-white rounded-full font-semibold hover:bg-gray-600 active:scale-95 transition text-xs sm:text-sm md:text-base flex-shrink-0"
        >
          ← Back
        </button>

        <button
          onClick={handleContinue}
          disabled={!selectedStyle}
          className={`
            flex-1 max-w-sm px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-full font-bold text-xs sm:text-sm md:text-base transition-all transform active:scale-95
            ${selectedStyle
              ? 'bg-[#e5181e] text-white shadow-2xl hover:scale-105 hover:bg-[#c01419]'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue →
        </button>
      </div>

      {/* Paginated Grid of Style Thumbnails */}
      <div className="flex-1 flex items-center justify-center px-2 relative min-h-0">
        {/* Left Arrow Button */}
        {totalPages > 1 && (
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className={`
              absolute left-0 top-1/2 transform -translate-y-1/2 z-10
              w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center
              transition-all active:scale-95
              ${currentPage === 0
                ? 'bg-gray-600/30 text-gray-500 cursor-not-allowed'
                : 'bg-[#e5181e] text-white shadow-lg hover:bg-[#c01419] hover:scale-110'
              }
            `}
          >
            <span className="text-lg sm:text-xl md:text-2xl font-bold">‹</span>
          </button>
        )}

        {/* Style Grid - 4 columns × 2 rows */}
        <div className="grid grid-cols-4 gap-2 sm:gap-2.5 md:gap-3 max-w-4xl mx-auto w-full">
          {currentStyles.map((styleKey) => (
            <StyleThumbnail
              key={styleKey}
              style={STYLES[styleKey]}
              isSelected={selectedStyle === styleKey}
              onSelect={() => handleStyleClick(styleKey)}
            />
          ))}
        </div>

        {/* Right Arrow Button */}
        {totalPages > 1 && (
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            className={`
              absolute right-0 top-1/2 transform -translate-y-1/2 z-10
              w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center
              transition-all active:scale-95
              ${currentPage === totalPages - 1
                ? 'bg-gray-600/30 text-gray-500 cursor-not-allowed'
                : 'bg-[#e5181e] text-white shadow-lg hover:bg-[#c01419] hover:scale-110'
              }
            `}
          >
            <span className="text-lg sm:text-xl md:text-2xl font-bold">›</span>
          </button>
        )}
      </div>

      {/* Page Indicator */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 text-center mb-1 mt-1 sm:mb-1.5 sm:mt-1.5">
          <div className="flex items-center justify-center gap-1.5 sm:gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <div
                key={index}
                className={`
                  rounded-full transition-all
                  ${index === currentPage
                    ? 'bg-[#e5181e] w-4 sm:w-5 md:w-6 h-1.5 sm:h-2'
                    : 'bg-white/30 w-1.5 sm:w-2 h-1.5 sm:h-2'
                  }
                `}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StyleSelectionScreen;
